import { useState } from "react";
import { useCustomMutation, useTranslate } from "@refinedev/core";
import { Button, Modal, TextareaField } from "@fawkes/ui";

import type { CommTemplateDetailData } from "../commTypes";

/**
 * "Aanpassen met AI" op een stuk geselecteerde tekst.
 *
 * <p>Het AI-paneel ernaast herschrijft het hele sjabloon. Dat is wat je wilt voor "formeler" of
 * "korter", maar niet voor "hier moet de betaalwijze staan": dan verwacht je dat alleen die zin
 * verandert, en kreeg je een voorstel voor het hele document terug (Maarten, 2026-10-23).
 *
 * <p>Daarom gaat hier alleen de selectie naar de server ({@code selectionHtml}) en vervangt Doorvoeren
 * alleen de selectie. Een voorstel kun je bijschaven: je typt wat er nog anders moet en het vorige
 * voorstel gaat mee als vertrekpunt, zodat je niet opnieuw hoeft te beginnen.
 *
 * <p>Net als in het paneel geldt: een voorstel dat de server niet placeholder-veilig noemt is niet door
 * te voeren. Een kapot {Dier.Naam} in een sjabloon merk je pas bij de afdruk.
 */

interface Voorstel {
  proposalHtml: string;
  summary: string;
  warnings: string[];
  placeholdersPreserved: boolean;
  aiUsed: boolean;
}

export function CommAiSelectieDialoog({
  open,
  template,
  selectieHtml,
  onSluit,
  onDoorvoeren,
}: {
  open: boolean;
  template: CommTemplateDetailData;
  /** De HTML van wat de gebruiker had geselecteerd toen hij het menu opende. */
  selectieHtml: string;
  onSluit: () => void;
  /** Vervangt de selectie in de editor door deze HTML. */
  onDoorvoeren: (html: string) => void;
}) {
  const t = useTranslate();
  const { mutate, isLoading } = useCustomMutation();
  const [opdracht, setOpdracht] = useState("");
  const [voorstel, setVoorstel] = useState<Voorstel | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  function vraag() {
    if (!opdracht.trim()) return;
    setFout(null);
    mutate(
      {
        url: "communicatie/templates/ai/rewrite",
        method: "post",
        values: {
          templateId: template.id,
          action: "custom",
          // Alleen de selectie. Het hele sjabloon gaat bewust niet mee: de server herschrijft wat hij
          // krijgt, en dan zou een opdracht over één zin het hele document raken.
          selectionHtml: voorstel ? voorstel.proposalHtml : selectieHtml,
          language: template.languageCode,
          templateType: template.type,
          category: template.category,
          customInstruction: opdracht.trim(),
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          setVoorstel(res.data as unknown as Voorstel);
          setOpdracht("");
        },
        onError: (e) => setFout(e?.message ?? t("communicatieAi.error.generic")),
      },
    );
  }

  function sluit() {
    setOpdracht("");
    setVoorstel(null);
    setFout(null);
    onSluit();
  }

  const ongewijzigd = voorstel != null && voorstel.proposalHtml.trim() === selectieHtml.trim();
  const magDoorvoeren = voorstel != null && voorstel.placeholdersPreserved && !ongewijzigd;

  return (
    <Modal
      open={open}
      title={t("communicatieAi.selectie.titel")}
      onClose={sluit}
      footer={
        <>
          <Button variant="secondary" onClick={sluit}>
            {t("common.cancel")}
          </Button>
          <Button variant="secondary" disabled={isLoading || !opdracht.trim()} onClick={vraag}>
            {voorstel
              ? t("communicatieAi.selectie.bijschaven")
              : t("communicatieAi.selectie.vraag")}
          </Button>
          <Button
            variant="primary"
            disabled={!magDoorvoeren}
            onClick={() => {
              if (voorstel) onDoorvoeren(voorstel.proposalHtml);
              sluit();
            }}
          >
            {t("communicatieAi.selectie.doorvoeren")}
          </Button>
        </>
      }
    >
      <div className="ce-ai-selectie">
        <p className="fawkes-muted">{t("communicatieAi.selectie.uitleg")}</p>

        <section className="ce-ai-selectie__blok">
          <h4>{t("communicatieAi.selectie.huidig")}</h4>
          {/* Altijd je eigen selectie, ook als er al een voorstel ligt. Het voorstel kwam hier eerst
              overheen te staan, en dan heb je niets meer om mee te vergelijken -- terwijl vergelijken
              het enige is wat je in dit venster doet (Maarten, 2026-10-23). */}
          <div
            className="ce-ai-selectie__tekst"
            dangerouslySetInnerHTML={{ __html: selectieHtml }}
          />
        </section>

        {voorstel && (
          <section className="ce-ai-selectie__blok ce-ai-selectie__blok--voorstel">
            <h4>{t("communicatieAi.selectie.voorstel")}</h4>
            <div
              className="ce-ai-selectie__tekst"
              dangerouslySetInnerHTML={{ __html: voorstel.proposalHtml }}
            />
          </section>
        )}

        <TextareaField
          label={voorstel
            ? t("communicatieAi.selectie.opdrachtNog")
            : t("communicatieAi.selectie.opdracht")}
          placeholder={t("communicatieAi.selectie.opdracht.placeholder")}
          rows={3}
          value={opdracht}
          onChange={(e) => setOpdracht(e.target.value)}
        />

        {isLoading && <p className="fawkes-muted">{t("communicatieAi.busy")}</p>}

        {voorstel && (
          <section className="ce-ai-selectie__meldingen">
            {voorstel.summary && <p className="fawkes-muted">{voorstel.summary}</p>}
            {!voorstel.placeholdersPreserved && (
              <p className="fawkes-error" role="alert">
                {t("communicatieAi.selectie.velden-kapot")}
              </p>
            )}
            {ongewijzigd && (
              <p className="fawkes-error" role="alert">
                {t("communicatieAi.selectie.ongewijzigd")}
              </p>
            )}
            {voorstel.warnings.map((w) => (
              <p key={w} className="fawkes-muted">{w}</p>
            ))}
          </section>
        )}

        {fout && <p className="fawkes-error" role="alert">{fout}</p>}
      </div>
    </Modal>
  );
}
